import { useState, useEffect, useCallback, useRef } from 'react';
import {
  OptimizedMessage,
  getPagedMessages,
  messageCacheManager,
  unifiedWebSocketManager
} from '../services/api.ts';
import { convertWahaUrlToProxy, withWahaClinicContext } from '../utils/wahaProxy.ts';
import { getExpiredMediaContent } from '../utils/mediaRetention.ts';
import { conversationEventKey } from '../utils/conversationIdentity.ts';
import { reconcileConfirmedMessage, shouldDisplayOutgoingWebSocketMessage, mergeMessageDelivery } from '../utils/messageAttribution.ts';
import type { MessageDeliveryState } from '../utils/messageAttribution.ts';
import { workflowMessageAsset } from './useWorkflowMessageMedia.ts';

const isPersistedWorkflowMedia = (message: OptimizedMessage): boolean =>
  message.source_channel === 'official_whatsapp' && ['image', 'audio', 'video'].includes(message.type) &&
  Number.isSafeInteger(message.databaseMessageId) && Number(message.databaseMessageId) > 0 &&
  Boolean(workflowMessageAsset(message.content));

interface UseOptimizedMessagesOptions {
  pageSize?: number;
  initialLoadDelay?: number;
  onUpdateContact?: (phone: string, type: string, content: any) => void;
}

export const useOptimizedMessages = (
  contactPhone: string | null,
  options: UseOptimizedMessagesOptions = {}
): {
  messages: OptimizedMessage[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  loadMoreMessages: () => Promise<void>;
  updateMessageTracking: (localMessageId: string, providerMessageId?: string | null, status?: OptimizedMessage['status'], sentContent?: string) => void;
  sendMessage: (
    content: any,
    type?: 'text' | 'image' | 'audio' | 'video' | 'nps' | 'comment',
    localMessageId?: string,
    source_channel?: string,
    extra?: Partial<OptimizedMessage>
  ) => any;
} => {
  // 🔥 MELHORIA CRÍTICA: Memoizar options para evitar re-renders desnecessários
  const memoizedOptions = useRef(options);

  // Atualizar apenas quando as options realmente mudarem
  const optionsChanged = JSON.stringify(options) !== JSON.stringify(memoizedOptions.current);
  if (optionsChanged) {
    memoizedOptions.current = options;
  }

  const { pageSize = 30 } = memoizedOptions.current;

  const [messages, setMessages] = useState<OptimizedMessage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const messagesRef = useRef<OptimizedMessage[]>([]);
  const pendingDeliveryRef = useRef<{ scope: string; entries: Map<string, { providerId: string; state: MessageDeliveryState; receivedAt: number; channel?: string; databaseId?: number }> }>({ scope: '', entries: new Map() });

  const deliveryScope = () => typeof window === 'undefined' ? String(contactPhone) :
    ['user_type', 'user_id', 'client_id', 'clinic_id'].map(key => localStorage.getItem(key) || '').join(':') + ':' + contactPhone;
  const renderedDeliveryScope = deliveryScope();
  const activeDeliveryScopeRef = useRef(renderedDeliveryScope);
  activeDeliveryScopeRef.current = renderedDeliveryScope;
  const pendingDelivery = () => {
    const scope = deliveryScope();
    if (pendingDeliveryRef.current.scope !== scope) {
      pendingDeliveryRef.current = { scope, entries: new Map() };
    }
    const entries = pendingDeliveryRef.current.entries;
    entries.forEach((entry, key) => {
      if (Date.now() - entry.receivedAt > 120000) entries.delete(key);
    });
    return entries;
  };
  const mergePendingDelivery = (message: OptimizedMessage): OptimizedMessage => {
    let merged = message;
    pendingDelivery().forEach(pending => {
      if (pending.providerId !== message.providerMessageId ||
          (pending.channel && message.source_channel && pending.channel !== message.source_channel) ||
          (pending.databaseId && message.databaseMessageId && pending.databaseId !== message.databaseMessageId)) return;
      merged = { ...merged, ...mergeMessageDelivery(merged, pending.state) };
    });
    return merged;
  };

  // Ref para rastrear IDs locais já processados
  const processedLocalIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const getLocalMediaPublicUrl = (value: string): string | null => {
    const normalized = value.trim();
    if (normalized.startsWith('/api/arquivos/media/')) {
      return normalized;
    }
    if (/^client_\d+\/clinic_\d+\/(image|audio|video)\//.test(normalized)) {
      return `/api/arquivos/media/${normalized}`;
    }
    return null;
  };

  /**
   * Ajuste principal: 
   * Se for 'video', deixamos `url: ''` (sem placeholder).
   * Para imagem/áudio, ainda podemos usar placeholders se quiser.
   */
  const processMediaContent = (content: any, mediaType: string): any => {
    const expiredContent = getExpiredMediaContent(content, mediaType);
    if (expiredContent) {
      return expiredContent;
    }

    // 🔧 FIX: Log específico para áudio
    if (mediaType === 'audio') {
      console.log("🎵 [processMediaContent] Processando ÁUDIO:");
    }

    // 🔥 FIX: Log específico para vídeo
    if (mediaType === 'video') {
      console.log("🎥 [processMediaContent] Processando VÍDEO:");
    }

    // O histórico de anexos com legenda é serializado como {url, caption}.
    if (typeof content === 'string' && content.trim().startsWith('{')) {
      try {
        const media = JSON.parse(content);
        if (media && typeof media.url === 'string') return processMediaContent(media, mediaType);
      } catch {
        // Mantém o tratamento existente para conteúdos que não sejam JSON de mídia.
      }
    }

    // Se já for objeto com url ou audioUrl (formato WAHA)
    if (typeof content === 'object') {
      // Para áudio, WAHA pode enviar como {audioUrl: ...}
      if (mediaType === 'audio' && content.audioUrl) {
        console.log("🎵 [processMediaContent] Áudio com audioUrl detectado");
        const localPublicUrl = getLocalMediaPublicUrl(String(content.audioUrl));
        if (localPublicUrl) {
          return { url: localPublicUrl, mediaPath: content.audioUrl, needsLoading: false };
        }
        const processedUrl = convertWahaUrlToProxy(String(content.audioUrl));
        return { url: processedUrl, mediaPath: content.audioUrl, needsLoading: true };
      }
      // Para vídeo, WAHA pode enviar como {videoUrl: ...}
      if (mediaType === 'video' && content.videoUrl) {
        console.log("🎥 [processMediaContent] Vídeo com videoUrl detectado");
        const localPublicUrl = getLocalMediaPublicUrl(String(content.videoUrl));
        if (localPublicUrl) {
          return { url: localPublicUrl, mediaPath: content.videoUrl, needsLoading: false };
        }
        const processedUrl = convertWahaUrlToProxy(String(content.videoUrl));
        return { url: processedUrl, mediaPath: content.videoUrl, needsLoading: true };
      }
      // Formato padrão com url
      if (content.url) {
        console.log("[processMediaContent] Conteúdo já é objeto com URL");
        const localPublicUrl = getLocalMediaPublicUrl(String(content.url));
        if (localPublicUrl) {
          return { ...content, url: localPublicUrl, mediaPath: content.url, needsLoading: false };
        }
        // 🔥 FIX: Converter URLs WAHA em objetos também
        if (content.url.includes('localhost:3000/api/files/')) {
          const proxyUrl = convertWahaUrlToProxy(content.url);
          console.log("[processMediaContent] URL WAHA em objeto convertida para proxy:");
          return { ...content, url: proxyUrl, mediaPath: content.url, needsLoading: true };
        }
        // 🔥 FIX: Se já for URL de proxy WAHA, usar diretamente
        if (content.url.startsWith('/api/waha/media/')) {
          console.log("[processMediaContent] URL de proxy WAHA em objeto detectada, usando diretamente:");
          return {
            ...content,
            url: withWahaClinicContext(content.url),
            mediaPath: content.url,
            needsLoading: true,
          };
        }
        return content;
      }
    }

    // Se for string
    if (typeof content === 'string') {
      // 🔥 FIX: Se for string vazia (vídeo do banco sem path), retornar erro
      if (content.trim() === '') {

        return {
          url: '',
          error: 'Conteúdo de mídia vazio',
          fallbackText: mediaType === 'video' ? '🎥 Vídeo (indisponível)' : '📎 Mídia (indisponível)',
          needsLoading: false
        };
      }

      // Se já for URL ou data:
      if (content.startsWith('http') || content.startsWith('blob:') || content.startsWith('data:')) {
        console.log("[processMediaContent] Conteúdo é URL/blob/data, verificando se precisa de proxy WAHA");

        // 🔥 FIX: Converter URLs WAHA localhost para proxy
        if (content.includes('localhost:3000/api/files/')) {
          const proxyUrl = convertWahaUrlToProxy(content);
          console.log("[processMediaContent] URL WAHA convertida para proxy:");
          return { url: proxyUrl, mediaPath: content, needsLoading: true };
        }

        return { url: content };
      }

      // 🔥 FIX: Se já for URL de proxy WAHA, usar diretamente
      if (content.startsWith('/api/waha/media/')) {
        console.log("[processMediaContent] URL de proxy WAHA detectada, usando diretamente:");
        return { url: withWahaClinicContext(content), mediaPath: content, needsLoading: true };
      }

      const localPublicUrl = getLocalMediaPublicUrl(content);
      if (localPublicUrl) {

        return {
          url: localPublicUrl,
          mediaPath: content,
          needsLoading: false,
          provider: 'local'
        };
      }

      // 🔧 FIX: Para áudio via WAHA, o content pode vir como path relativo
      // Exemplo: "client_1/clinic_68/audio/arquivo.oga" ou "audio/arquivo.mp3"
      if (mediaType === 'audio' && (content.includes('/audio/') || content.includes('.oga') || content.includes('.mp3'))) {
        console.log("🎵 [processMediaContent] Áudio com path WAHA detectado, criando URL de mídia");
        // 🔥 FIX: Detectar formato do path para evitar duplicação
        let wahaProxyUrl;

        // Caso 1: Path completo com client_X/clinic_Y (do banco de follow-up)
        // Exemplo: "client_68/clinic_68/audio/arquivo.mp3"
        if (content.match(/^client_\d+\/clinic_\d+\//)) {
          // Extrair apenas clinic_X/... do path
          const clinicPart = content.replace(/^client_\d+\//, '');
          wahaProxyUrl = `/api/waha/media/${clinicPart}`;
          console.log("🎵 [processMediaContent] Path completo detectado, usando:");
        }
        // Caso 2: Path já começa com clinic_X (correto)
        else if (content.startsWith('clinic_')) {
          wahaProxyUrl = `/api/waha/media/${content}`;
          console.log("🎵 [processMediaContent] Path com clinic_ detectado, usando:");
        }
        // Caso 3: Path parcial (audio/arquivo.mp3), adicionar clinic_id
        else {
          const clinicId = localStorage.getItem('clinic_id');
          if (clinicId) {
            wahaProxyUrl = `/api/waha/media/clinic_${clinicId}/${content}`;
            console.log("🎵 [processMediaContent] Path parcial detectado, adicionando clinic_id:");
          } else {
            wahaProxyUrl = `/api/waha/media/${content}`;
            console.log("🎵 [processMediaContent] Sem clinic_id, usando path direto:");
          }
        }

        return {
          url: withWahaClinicContext(wahaProxyUrl),
          mediaPath: content,
          needsLoading: true,
          provider: 'waha',
          fallbackText: '🎵 Áudio' // Texto fallback se falhar carregamento
        };
      }

      // 🔥 FIX: Para vídeo via WAHA, o content pode vir como path relativo
      // Exemplo: "client_68/clinic_68/video/arquivo.mp4" ou "video/arquivo.mp4"
      if (mediaType === 'video' && (content.includes('/video/') || content.includes('.mp4') || content.includes('.mov') || content.includes('.avi'))) {
        console.log("🎥 [processMediaContent] Vídeo com path WAHA detectado, criando URL de mídia:");
        // 🔥 FIX: Detectar formato do path para evitar duplicação (igual ao áudio)
        let wahaProxyUrl;

        // Caso 1: Path completo com client_X/clinic_Y (do banco de follow-up)
        // Exemplo: "client_68/clinic_68/video/arquivo.mp4"
        if (content.match(/^client_\d+\/clinic_\d+\//)) {
          // Extrair apenas clinic_X/... do path
          const clinicPart = content.replace(/^client_\d+\//, '');
          wahaProxyUrl = `/api/waha/media/${clinicPart}`;
          console.log("🎥 [processMediaContent] Path completo detectado, usando:");
        }
        // Caso 2: Path já começa com clinic_X (correto)
        else if (content.startsWith('clinic_')) {
          wahaProxyUrl = `/api/waha/media/${content}`;
          console.log("🎥 [processMediaContent] Path com clinic_ detectado, usando:");
        }
        // Caso 3: Path já é uma URL proxy completa
        else if (content.startsWith('/api/waha/media/')) {
          wahaProxyUrl = content;
          console.log("🎥 [processMediaContent] URL proxy detectada, usando:");
        }
        // Caso 4: Path parcial (video/arquivo.mp4), adicionar clinic_id
        else {
          const clinicId = localStorage.getItem('clinic_id');
          if (clinicId) {
            wahaProxyUrl = `/api/waha/media/clinic_${clinicId}/${content}`;
            console.log("🎥 [processMediaContent] Path parcial detectado, adicionando clinic_id:");
          } else {
            wahaProxyUrl = `/api/waha/media/${content}`;
            console.log("🎥 [processMediaContent] Sem clinic_id, usando path direto:");
          }
        }

        return {
          url: withWahaClinicContext(wahaProxyUrl),
          mediaPath: content,
          needsLoading: true,
          provider: 'waha',
          fallbackText: '🎥 Vídeo' // Texto fallback se falhar carregamento
        };
      }

      // Se for image/video/audio local, geramos { mediaPath, needsLoading, url="" ou placeholder... }
      if (mediaType === 'image' || mediaType === 'video' || mediaType === 'audio') {
        // 🔥 FIX: Evitar processar mídias vazias aqui
        if (content.trim() === '') {

          return {
            url: '',
            error: 'Conteúdo de mídia vazio',
            fallbackText: mediaType === 'video' ? '🎥 Vídeo (indisponível)' : '📎 Mídia (indisponível)',
            needsLoading: false
          };
        }

        // Atribui 'mediaPath' e 'needsLoading'
        const mediaPath = content;
        return {
          mediaPath,
          needsLoading: true,
          // Diferença: para vídeo e áudio, não colocamos placeholder.
          url: mediaType === 'image'
            ? '/assets/image-placeholder.png'
            : '' // Vazio para áudio e vídeo
        };
      }

      // Se não for image/video/audio, retorna como texto
      return content;
    }

    // Caso padrão
    return content;
  };

  // Processa array de mensagens para converter mídias em URLs (objeto {mediaPath, ...})
  const processMessagesMedia = (msgs: OptimizedMessage[]): OptimizedMessage[] => {
    return msgs.map(msg => {
      if (msg.type === 'image' || msg.type === 'video' || msg.type === 'audio') {
        return {
          ...msg,
          content: processMediaContent(msg.content, msg.type)
        };
      }
      return msg;
    });
  };

  const getIncomingMessageId = (message: any): string | undefined => {
    const dbId = Number(message?.dbMessageId);
    const id = Number.isInteger(dbId) && dbId > 0 ? dbId
      : message?.source_channel === 'instagram' ? (message?.id || message?.messageId)
      : message?.messageId || message?.id || message?.localMessageId;
    return id ? String(id) : undefined;
  };

  const getIncomingTimestamp = (message: any): number => {
    const rawTimestamp = message?.timestampNumber || message?.timestamp || message?.momment;

    if (typeof rawTimestamp === 'number') {
      return rawTimestamp > 10_000_000_000 ? rawTimestamp : rawTimestamp * 1000;
    }

    if (typeof rawTimestamp === 'string' && rawTimestamp.trim()) {
      const numericTimestamp = Number(rawTimestamp);
      if (Number.isFinite(numericTimestamp)) {
        return numericTimestamp > 10_000_000_000 ? numericTimestamp : numericTimestamp * 1000;
      }

      const parsedTimestamp = Date.parse(rawTimestamp);
      if (Number.isFinite(parsedTimestamp)) {
        return parsedTimestamp;
      }
    }

    return Date.now();
  };

  const isMediaMessageType = (type: string): boolean =>
    type === 'image' || type === 'video' || type === 'audio';

  const isLocalOptimisticMessage = (message: OptimizedMessage): boolean => {
    const maybeLocal = message as OptimizedMessage & { localMessageId?: string };
    return Boolean(
      message.status === 'sending' ||
      message.id?.startsWith('local_') ||
      maybeLocal.localMessageId?.startsWith('local_')
    );
  };

  const shouldReplaceLocalEcho = (
    existing: OptimizedMessage,
    incoming: any,
    incomingId: string | undefined,
    incomingTimestamp: number
  ): boolean => {
    if (incoming?.fromMe !== true || existing.fromMe !== true) {
      return false;
    }

    if (existing.type !== incoming.type || !isLocalOptimisticMessage(existing)) {
      return false;
    }

    const maybeLocal = existing as OptimizedMessage & { localMessageId?: string };
    const localId = maybeLocal.localMessageId || existing.id;

    if (incoming.localMessageId && incoming.localMessageId === localId) {
      return true;
    }

    if (incomingId && incomingId === existing.id) {
      return true;
    }

    const timeDiff = Math.abs((existing.timestampNumber || 0) - incomingTimestamp);

    if (incoming.type === 'text') {
      return existing.content === incoming.content && timeDiff < 30_000;
    }

    if (isMediaMessageType(incoming.type)) {
      return timeDiff < 2 * 60_000;
    }

    return false;
  };

  const processIncomingContent = (message: any): any => {
    if (message.type === 'image' || message.type === 'video' || message.type === 'audio') {
      return processMediaContent(message.content, message.type);
    }

    if (message.type === 'nps') {
      return {
        nps_data: {
          question: message.content || 'Em uma escala de 1 a 5, como você avalia nosso atendimento?',
          status: 'sent',
          message_id: message.messageId || `nps_${Date.now()}`
        }
      };
    }

    return message.content;
  };

  const buildWebSocketMessage = (
    message: any,
    processedContent: any,
    incomingId: string | undefined,
    timestampNumber: number,
    displayTimestamp?: string
  ): OptimizedMessage => mergePendingDelivery({
    id: incomingId || `${message.phone}_${Date.now()}`,
    type: message.type,
    content: processedContent,
    sender: {
      phone: message.fromMe ? 'me' : message.phone,
      name: message.senderName || (message.fromMe ? 'Você' : 'Desconhecido'),
      photo: message.photo || ''
    },
    timestamp: displayTimestamp || new Date(timestampNumber).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    timestampNumber,
    fromMe: message.fromMe,
    status: message.status || (message.fromMe ? 'sent' : 'delivered'),
    ack: message.ack,
    templateSnapshot: message.templateSnapshot,
    deliveryError: message.deliveryError,
    deliveryStatusAt: message.deliveryStatusAt,
    source_channel: message.source_channel || message.active_channel,
    providerMessageId: message.providerMessageId || message.messageId || (message.dbMessageId ? null : incomingId) || null,
    replyTo: message.replyTo || null,
    reactions: message.reactions || [],
    author_type: message.author_type || null,
    author_id: message.author_id ?? null,
    author_name: message.author_name ?? null,
    author_evidence: message.author_evidence ?? null,
    agent_run_id: message.agent_run_id ?? null,
    learning_action: message.learning_action,
    databaseMessageId: Number.isInteger(Number(message.dbMessageId)) && Number(message.dbMessageId) > 0 ? Number(message.dbMessageId) : null
  });

  /**
   * 🔥 FIX: Função auxiliar para detectar duplicatas
   * Usa os mesmos critérios do WebSocket handler (linhas 565-647)
   */
  const isDuplicateMessage = (newMsg: OptimizedMessage, existingMsg: OptimizedMessage): boolean => {
    if (newMsg.source_channel === 'instagram' || existingMsg.source_channel === 'instagram') {
      return newMsg.source_channel === existingMsg.source_channel && (newMsg.id === existingMsg.id
        || Boolean(newMsg.providerMessageId && newMsg.providerMessageId === existingMsg.providerMessageId));
    }
    // Distinct durable workflow receipts can intentionally send the same asset.
    // Time/content matching cannot identify those operations.
    if (isPersistedWorkflowMedia(newMsg) || isPersistedWorkflowMedia(existingMsg)) {
      return Boolean(newMsg.databaseMessageId && newMsg.databaseMessageId === existingMsg.databaseMessageId &&
        (!newMsg.providerMessageId || !existingMsg.providerMessageId || newMsg.providerMessageId === existingMsg.providerMessageId));
    }
    // Check 1: Mesmo ID
    if (existingMsg.id === newMsg.id) {
      console.log("🔄 [isDuplicateMessage] Duplicata por mesmo ID:");
      return true;
    }

    // Check 2: Texto - comparar conteúdo, fromMe e timestamp
    if (newMsg.type === 'text' && existingMsg.type === 'text') {
      if (existingMsg.content === newMsg.content && existingMsg.fromMe === newMsg.fromMe) {
        const timeDiff = Math.abs((existingMsg.timestampNumber || 0) - (newMsg.timestampNumber || 0));
        if (timeDiff < 5000) { // 5 segundos
          console.log("📝 [isDuplicateMessage] Duplicata de texto:");
          return true;
        }
      }
      return false;
    }

    // 🔥 FIX: Para mensagens de mídia fromMe=true, usar critério MAIS AGRESSIVO
    // (timestamp próximo + mesmo tipo + fromMe=true é suficiente para detectar duplicata)
    if ((newMsg.type === 'audio' || newMsg.type === 'video' || newMsg.type === 'image') &&
      newMsg.type === existingMsg.type &&
      newMsg.fromMe === true && existingMsg.fromMe === true) {

      const timeDiff = Math.abs((existingMsg.timestampNumber || 0) - (newMsg.timestampNumber || 0));

      // 🔥 MELHORIA: Janela temporal diferente por tipo de mídia
      // Vídeos podem demorar mais para processar, então janela maior
      const timeWindow = newMsg.type === 'video' ? 15000 : 5000; // 15s para vídeo, 5s para áudio/imagem

      // 🔥 DEBUG: Log detalhado para vídeos
      if (newMsg.type === 'video') {
        console.log("🎥 [isDuplicateMessage] Comparando vídeos fromMe=true:");
      }

      // Janela temporal adaptativa por tipo de mídia
      if (timeDiff < timeWindow) {

        return true;
      } else {
        // 🔥 DEBUG: Log quando NÃO detecta como duplicata
        if (newMsg.type === 'video') {
          console.log("🎥 [isDuplicateMessage] ❌ NÃO É DUPLICATA (timeDiff > 5s):");
        }
      }
    }

    // Check 3: Áudio - verificar proximidade temporal (fallback para fromMe=false)
    if (newMsg.type === 'audio' && existingMsg.type === 'audio') {
      const timeDiff = Math.abs((existingMsg.timestampNumber || 0) - (newMsg.timestampNumber || 0));
      if (timeDiff < 1000 && existingMsg.fromMe === newMsg.fromMe) { // 1 segundo
        console.log("🎵 [isDuplicateMessage] Duplicata de áudio por proximidade temporal:");
        return true;
      }
      return false;
    }

    // Check 4: Vídeo - comparar URLs (fallback se não pegou acima)
    if (newMsg.type === 'video' && existingMsg.type === 'video') {
      const newVideoUrl = typeof newMsg.content === 'object' ? newMsg.content?.url : undefined;
      const existingVideoUrl = typeof existingMsg.content === 'object' ? existingMsg.content?.url : undefined;

      if (newVideoUrl && existingVideoUrl && newVideoUrl === existingVideoUrl) {
        const timeDiff = Math.abs((existingMsg.timestampNumber || 0) - (newMsg.timestampNumber || 0));
        if (timeDiff < 10000) { // 10 segundos
          console.log("🎥 [isDuplicateMessage] Duplicata de vídeo (mesma URL):");
          return true;
        }
      }
      return false;
    }

    // Check 5: Imagem - comparar URLs (fallback se não pegou acima)
    if (newMsg.type === 'image' && existingMsg.type === 'image') {
      const newImageUrl = typeof newMsg.content === 'object' ? newMsg.content?.url : undefined;
      const existingImageUrl = typeof existingMsg.content === 'object' ? existingMsg.content?.url : undefined;

      if (newImageUrl && existingImageUrl && newImageUrl === existingImageUrl) {
        const timeDiff = Math.abs((existingMsg.timestampNumber || 0) - (newMsg.timestampNumber || 0));
        if (timeDiff < 10000) { // 10 segundos
          console.log("🖼️ [isDuplicateMessage] Duplicata de imagem (mesma URL):");
          return true;
        }
      }
      return false;
    }

    return false;
  };

  const getMessageContentSignature = (message: OptimizedMessage): string => {
    const content = message.content as any;

    if (typeof content === 'string') {
      return content.length > 500 ? content.slice(0, 500) : content;
    }

    if (content && typeof content === 'object') {
      const url = content.url || content.mediaPath || content.audioUrl || content.videoUrl || content.imageUrl;
      if (url) {
        return String(url).slice(0, 500);
      }

      if (content.nps_data?.message_id) {
        return String(content.nps_data.message_id);
      }

      return JSON.stringify(content).slice(0, 500);
    }

    return '';
  };

  const getDeduplicationKey = (message: OptimizedMessage): string => {
    if (isPersistedWorkflowMedia(message)) return `workflow_media|${message.databaseMessageId}|${message.providerMessageId || message.id}`;
    if (message.source_channel === 'instagram') return `instagram|${message.providerMessageId || message.id}`;
    if (message.type === 'template' && !message.id?.startsWith('local_')) {
      return `template|${message.source_channel || ''}|${message.providerMessageId || ''}|${message.databaseMessageId || message.id}`;
    }
    const timestampBucket = Math.floor((message.timestampNumber || 0) / 5000);
    const contentSignature = getMessageContentSignature(message);

    if (contentSignature) {
      return `${message.type}|${message.fromMe ? '1' : '0'}|${timestampBucket}|${contentSignature}`;
    }

    return `id|${message.id}`;
  };

  const shouldReplaceMessage = (existing: OptimizedMessage, candidate: OptimizedMessage): boolean => {
    if (existing.source_channel === 'instagram' && candidate.source_channel === 'instagram') {
      const rank = { failed: -1, sending: 0, sent: 1, delivered: 2, read: 3 };
      return (rank[candidate.status || 'sent'] ?? 0) > (rank[existing.status || 'sent'] ?? 0);
    }
    const existingIsLocal = existing.id?.startsWith('local_');
    const candidateIsLocal = candidate.id?.startsWith('local_');

    if (existingIsLocal && !candidateIsLocal) return true;
    if (existing.status === 'sending' && candidate.status && candidate.status !== 'sending') return true;

    return existing.id === candidate.id;
  };

  const shouldCollapseLocalPersistedMedia = (
    existing: OptimizedMessage,
    candidate: OptimizedMessage
  ): boolean => {
    if (isPersistedWorkflowMedia(existing) || isPersistedWorkflowMedia(candidate)) return false;
    if (existing.source_channel === 'instagram' || candidate.source_channel === 'instagram') return false;
    if (!isMediaMessageType(existing.type) || existing.type !== candidate.type) {
      return false;
    }
    if (existing.fromMe !== true || candidate.fromMe !== true) {
      return false;
    }

    const existingIsLocal = isLocalOptimisticMessage(existing);
    const candidateIsLocal = isLocalOptimisticMessage(candidate);
    if (existingIsLocal === candidateIsLocal) {
      return false;
    }

    const timeDiff = Math.abs((existing.timestampNumber || 0) - (candidate.timestampNumber || 0));
    return timeDiff < 2 * 60 * 1000;
  };

  const dedupeAndSortMessages = (items: OptimizedMessage[]): OptimizedMessage[] => {
    const uniqueMessages: OptimizedMessage[] = [];
    const sortedItems = [...items].sort((a, b) => (a.timestampNumber || 0) - (b.timestampNumber || 0));

    sortedItems.forEach((message) => {
      const key = getDeduplicationKey(message);
      if (message.source_channel !== 'instagram') {
        const sameIdentityIndex = uniqueMessages.findIndex(existing =>
          existing.id === message.id || Boolean(
            existing.providerMessageId && message.providerMessageId &&
            existing.providerMessageId === message.providerMessageId &&
            (!existing.source_channel || !message.source_channel || existing.source_channel === message.source_channel) &&
            (!existing.databaseMessageId || !message.databaseMessageId || existing.databaseMessageId === message.databaseMessageId)
          )
        );
        if (sameIdentityIndex >= 0) {
          uniqueMessages[sameIdentityIndex] = reconcileConfirmedMessage(uniqueMessages[sameIdentityIndex], message);
          return;
        }
      }
      const sameKeyIndex = uniqueMessages.findIndex(existing => getDeduplicationKey(existing) === key);

      if (sameKeyIndex >= 0) {
        if (shouldReplaceMessage(uniqueMessages[sameKeyIndex], message)) {
          uniqueMessages[sameKeyIndex] = message;
        }
        return;
      }

      const localPersistedIndex = uniqueMessages.findIndex(existing =>
        shouldCollapseLocalPersistedMedia(existing, message)
      );

      if (localPersistedIndex >= 0) {
        if (shouldReplaceMessage(uniqueMessages[localPersistedIndex], message)) {
          uniqueMessages[localPersistedIndex] = message;
        }
        return;
      }

      uniqueMessages.push(message);
    });

    return uniqueMessages.sort((a, b) => (a.timestampNumber || 0) - (b.timestampNumber || 0));
  };

  useEffect(() => {
    console.log("🔄 [useOptimizedMessages] useEffect executado:");

    if (!contactPhone) {
      console.log("🚫 [useOptimizedMessages] contactPhone é null, limpando estado");
      setMessages([]);
      setHasMore(false);
      setIsLoading(false);
      setIsLoadingMore(false);
      return;
    }

    // 🔥 FIX SIMPLES: Limpar mensagens imediatamente quando mudar de contato

    setMessages([]);
    setHasMore(false);
    setIsLoadingMore(false);

    let mounted = true;
    const subscribedDeliveryScope = deliveryScope();

    const loadInitialMessages = async () => {

      setIsLoading(true);
      setError(null);

      try {
        // 🔥 FIX: Cache com deduplicação ANTES de exibir
        console.log("[loadInitialMessages] Verificando cache...");
        const cachedMessages = messageCacheManager.getMessages(contactPhone);
        if (cachedMessages && cachedMessages.length > 0) {

          const processedCached = processMessagesMedia(cachedMessages).map(mergePendingDelivery);
          const dedupedCache = dedupeAndSortMessages(processedCached);


          console.log("[loadInitialMessages] Mostrando mensagens do cache deduplicado temporariamente");
          setMessages(dedupedCache);
          setIsLoading(false);
        } else {
          console.log("[loadInitialMessages] Nenhuma mensagem no cache");
        }

        // Sempre tenta API
        try {
          console.log("[loadInitialMessages] Buscando mensagens da API...");
          const resp = await getPagedMessages(contactPhone, pageSize);
          console.log("[loadInitialMessages] Resposta da API:");

          if (mounted && deliveryScope() === subscribedDeliveryScope && resp.messages && resp.messages.length > 0) {
            const processedApi = processMessagesMedia(resp.messages).map(mergePendingDelivery);


            setMessages(prev => {
              const combined = [...prev, ...processedApi];
              const unique = dedupeAndSortMessages(combined);

              messageCacheManager.saveMessages(contactPhone, unique);
              return unique;
            });
            setHasMore(resp.pagination.hasMore);
          } else if (mounted && deliveryScope() === subscribedDeliveryScope && (!cachedMessages || cachedMessages.length === 0)) {
            // Se não há mensagens da API e não há cache, definir array vazio
            setMessages([]);
            setHasMore(false);
          }
        } catch (apiError) {
          console.warn("[loadInitialMessages] Falha ao obter mensagens da API:");
          if ((!cachedMessages || cachedMessages.length === 0)) {
            setError(apiError instanceof Error ? apiError : new Error(String(apiError)));
          }
        }
      } catch (err) {
        if (mounted) {
          console.error("Erro ao carregar mensagens:");
          setError(err instanceof Error ? err : new Error(String(err)));
          setMessages([]);
          setHasMore(false);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    const handleNewMessage = (rawMessage: any) => {
      if (!mounted || deliveryScope() !== subscribedDeliveryScope) return;
      if (
        rawMessage?.type === 'contact_presence' ||
        rawMessage?.data?.type === 'contact_presence'
      ) {
        return;
      }

      if (rawMessage.type === 'message_status') {
        const statusPayload = rawMessage.data || rawMessage;
        const providerMessageId = statusPayload.providerMessageId || statusPayload.messageId;
        if (typeof providerMessageId !== 'string' || !providerMessageId || providerMessageId.length > 255 || conversationEventKey(rawMessage) !== contactPhone) return;
        if (statusPayload.clinic_id && localStorage.getItem('clinic_id') &&
            String(statusPayload.clinic_id) !== localStorage.getItem('clinic_id')) return;
        const entries = pendingDelivery();
        const channel = typeof statusPayload.source_channel === 'string' ? statusPayload.source_channel : undefined;
        const databaseId = Number.isInteger(Number(statusPayload.dbMessageId)) && Number(statusPayload.dbMessageId) > 0 ? Number(statusPayload.dbMessageId) : undefined;
        const receiptKey = `${providerMessageId}|${channel || ''}|${databaseId || ''}`;
        const previous = entries.get(receiptKey);
        entries.set(receiptKey, { providerId: providerMessageId, state: mergeMessageDelivery(previous?.state || {}, statusPayload), receivedAt: Date.now(), channel, databaseId });
        while (entries.size > 128) entries.delete(entries.keys().next().value);

        setMessages(prev => {
          const updated = prev.map(msg => {
            if ((channel && msg.source_channel && channel !== msg.source_channel) ||
                (databaseId && msg.databaseMessageId && databaseId !== msg.databaseMessageId)) return msg;
            const localId = (msg as OptimizedMessage & { localMessageId?: string }).localMessageId;
            if (
              msg.providerMessageId === providerMessageId ||
              msg.id === providerMessageId ||
              (typeof statusPayload.localMessageId === 'string' && statusPayload.localMessageId.length > 0 && localId === statusPayload.localMessageId)
            ) {
              return {
                ...msg,
                ...mergeMessageDelivery(msg, statusPayload),
                providerMessageId
              };
            }
            return msg;
          });
          messageCacheManager.saveMessages(contactPhone, updated);
          return updated;
        });
        return;
      }

      if (rawMessage.type === 'message_reaction') {
        const reactionPayload = rawMessage.data || rawMessage;
        const providerMessageId = reactionPayload.providerMessageId || reactionPayload.messageId;
        const actorId = reactionPayload.actorId || (reactionPayload.fromMe ? 'me' : 'contact');
        if (!providerMessageId || !actorId || conversationEventKey(rawMessage) !== contactPhone) return;

        setMessages(prev => {
          const updated = prev.map(msg => {
            if (msg.providerMessageId !== providerMessageId && msg.id !== providerMessageId) {
              return msg;
            }

            const existingReactions = msg.reactions || [];
            const withoutActor = existingReactions.filter(reaction => reaction.actorId !== actorId);
            const emoji = reactionPayload.emoji || reactionPayload.reaction || '';
            return {
              ...msg,
              reactions: emoji
                ? [
                    ...withoutActor,
                    {
                      actorId,
                      actorName: reactionPayload.actorName,
                      emoji,
                      fromMe: reactionPayload.fromMe,
                      timestamp: reactionPayload.timestamp || new Date().toISOString()
                    }
                  ]
                : withoutActor
            };
          });
          messageCacheManager.saveMessages(contactPhone, updated);
          return updated;
        });
        return;
      }

      // FIX: Unwrap message if it comes wrapped in 'data' (WebSocket standard)
      const message = rawMessage.data ? rawMessage.data : rawMessage;

      console.log("📨 [useOptimizedMessages] Nova mensagem recebida:");

      // Log apenas para debug
      if (message.phone === contactPhone) {

      }

      if (message.type === 'contact_mode_changed' ||
        message.content === '[CONTACT_MODE_CHANGED]' ||
        (typeof message.content === 'string' &&
          message.content.includes('CONTACT_MODE_CHANGED'))) {
        console.log("Mensagem de modo ignorada:");
        return;
      }

      // 🔥 DEBUG: Logs detalhados para todas as mensagens
      console.log("🔍 [useOptimizedMessages] Verificando filtros:");

      // 🔥 FIX CRÍTICO: Reabilitar filtro por contato
      // Evita que mensagens de outros contatos apareçam na conversa atual
      if (conversationEventKey(rawMessage) !== contactPhone) {
        console.log("🚫 [useOptimizedMessages] IGNORANDO mensagem de contato diferente:");
        return;
      }

      // Filtro para mensagens fromMe (evita duplicatas)
      if (!shouldDisplayOutgoingWebSocketMessage(message)) {
        console.log("🚫 [useOptimizedMessages] IGNORANDO mensagem de saída sem autoria confirmada via WebSocket:");
        return; // Bloqueia apenas mensagens fromMe=true que não vieram da API
      }

      // Tratar atualizações NPS específicas
      if (message.type === 'nps_update') {
        console.log("🎯 [useOptimizedMessages] Recebeu nps_update:");

        if (mounted && message.phone === contactPhone) {
          setMessages(prev => {
            return prev.map(msg => {
              // Encontrar a mensagem NPS pelo poll_message_id
              if (msg.type === 'nps') {
                let parsedContent: any;
                let npsData: any;

                // Parse do conteúdo (pode ser string JSON ou objeto)
                try {
                  if (typeof msg.content === 'string') {
                    parsedContent = JSON.parse(msg.content);
                  } else if (typeof msg.content === 'object') {
                    parsedContent = msg.content;
                  } else {
                    return msg; // Conteúdo inválido
                  }

                  npsData = parsedContent.nps_data;
                  if (!npsData || !npsData.message_id) {
                    return msg; // Não tem estrutura NPS válida
                  }

                } catch (e) {
                  console.log("⚠️ [useOptimizedMessages] Erro ao fazer parse do conteúdo NPS:");
                  return msg;
                }

                // Verificar se é a mensagem NPS que estamos procurando
                if (npsData.message_id === message.poll_message_id) {
                  const updatedNpsData = {
                    ...npsData,
                    status: 'answered' as const,
                    score: message.score,
                    answered_at: message.answered_at
                  };

                  const updatedContent = {
                    ...parsedContent,
                    nps_data: updatedNpsData
                  };

                  console.log("🎯 [useOptimizedMessages] Atualizando mensagem NPS:");

                  return {
                    ...msg,
                    content: typeof msg.content === 'string'
                      ? JSON.stringify(updatedContent)
                      : updatedContent
                  };
                }
              }
              return msg;
            });
          });
        }
        return; // Não processar como mensagem normal
      }

      // 🔧 FIX: Extrair conteúdo de mídia do formato WAHA
      // WAHA envia mídias em formatos específicos:
      // - { audio: { audioUrl: "..." } }
      // - { image: { imageUrl: "...", caption: "..." } }
      // - { video: { videoUrl: "...", caption: "..." } }
      if (message.type === 'audio' && message.audio) {
        console.log("🎵 [useOptimizedMessages] Extraindo áudio do formato WAHA:");
        message.content = message.audio.audioUrl || message.audio;
      } else if (message.type === 'image' && message.image) {
        console.log("🖼️ [useOptimizedMessages] Extraindo imagem do formato WAHA:");
        message.content = {
          url: message.image.imageUrl || message.image,
          caption: message.image.caption
        };
      } else if (message.type === 'video' && message.video) {
        console.log("🎥 [useOptimizedMessages] Extraindo vídeo do formato WAHA:");
        console.log("🎥 [DEBUG] Video details:");
        message.content = {
          url: message.video.videoUrl || message.video,
          caption: message.video.caption
        };
      }

      // Atualiza só se o phone bater (já verificado acima)
      if (mounted) {
        const incomingId = getIncomingMessageId(message);
        const incomingTimestamp = getIncomingTimestamp(message);
        const processedContent = processIncomingContent(message);
        const newMessage = buildWebSocketMessage(
          message,
          processedContent,
          incomingId,
          incomingTimestamp
        );

        setMessages(prev => {
          const localEchoIndex = prev.findIndex(existing =>
            !isPersistedWorkflowMedia(newMessage) && shouldReplaceLocalEcho(existing, message, incomingId, incomingTimestamp)
          );

          if (localEchoIndex >= 0) {
            const localMessage = prev[localEchoIndex];
            const replacement: OptimizedMessage = {
              ...newMessage,
              timestamp: localMessage.timestamp,
              timestampNumber: localMessage.timestampNumber || newMessage.timestampNumber,
              replyTo: newMessage.replyTo || localMessage.replyTo,
              reactions: newMessage.reactions?.length ? newMessage.reactions : localMessage.reactions,
            };
            const updated = [...prev];
            updated[localEchoIndex] = replacement;
            const unique = dedupeAndSortMessages(updated);
            messageCacheManager.saveMessages(contactPhone, unique);
            console.log("🔁 [useOptimizedMessages] Eco WebSocket reconciliado com mensagem local:");
            return unique;
          }

          if (newMessage.databaseMessageId || newMessage.providerMessageId) {
            const confirmedIndex = prev.findIndex(existing =>
              existing.id === incomingId || Boolean(
                newMessage.providerMessageId && existing.providerMessageId === newMessage.providerMessageId &&
                (!existing.source_channel || !newMessage.source_channel || existing.source_channel === newMessage.source_channel) &&
                (!existing.databaseMessageId || !newMessage.databaseMessageId || existing.databaseMessageId === newMessage.databaseMessageId)
              )
            );
            if (confirmedIndex >= 0) {
              const updated = [...prev];
              updated[confirmedIndex] = reconcileConfirmedMessage(updated[confirmedIndex], newMessage);
              const unique = dedupeAndSortMessages(updated);
              messageCacheManager.saveMessages(contactPhone, unique);
              return unique;
            }
          }

          if (incomingId && prev.some(m => m.id === incomingId)) {
            console.log("🔄 [useOptimizedMessages] Duplicata detectada por messageId:");
            return prev;
          }

          const isDuplicate = prev.some(existing => isDuplicateMessage(newMessage, existing));
          if (isDuplicate) {
            console.log("Mensagem duplicada ignorada:");
            return prev;
          }

          console.log("✅ [useOptimizedMessages] OptimizedMessage criada com source_channel:");

          const updated = [...prev, newMessage].sort((a, b) =>
            (a.timestampNumber || 0) - (b.timestampNumber || 0)
          );

          messageCacheManager.saveMessages(contactPhone, updated);
          return updated;
        });
      }
    };

    // Inscrever no tópico do telefone específico
    console.log("🔔 [useOptimizedMessages] Inscrevendo no tópico:");
    unifiedWebSocketManager.subscribe(contactPhone);

    // Registrar handler para mensagens deste telefone
    console.log("🎯 [useOptimizedMessages] Registrando handler para:");
    const unsubscribe = unifiedWebSocketManager.onMessage(contactPhone, handleNewMessage);

    // Log de inscrição


    loadInitialMessages();

    return () => {
      mounted = false;
      unsubscribe();
      // Desinscrever do tópico quando o componente desmontar
      unifiedWebSocketManager.unsubscribe(contactPhone);
    };
  }, [contactPhone, memoizedOptions.current.pageSize, memoizedOptions.current.initialLoadDelay, optionsChanged]);

  const loadMoreMessages = useCallback(async () => {
    if (!contactPhone || !hasMore || isLoading || isLoadingMore) return;
    const requestDeliveryScope = deliveryScope();
    setIsLoadingMore(true);
    try {
      const oldest = messages.length > 0 ? messages[0] : null;
      let resp: Awaited<ReturnType<typeof getPagedMessages>>;
      if (oldest) {
        resp = await getPagedMessages(contactPhone, pageSize, oldest.id, undefined);
      } else {
        resp = await getPagedMessages(contactPhone, pageSize);
      }

      if (requestDeliveryScope !== activeDeliveryScopeRef.current || requestDeliveryScope !== deliveryScope()) return;
      if (resp.messages.length > 0) {
        const processed = processMessagesMedia(resp.messages).map(mergePendingDelivery);
        setMessages(prev => {
          const combined = [...processed, ...prev];
          const unique = dedupeAndSortMessages(combined);

          messageCacheManager.saveMessages(contactPhone, unique);
          return unique;
        });
      }

      setHasMore(resp.pagination.hasMore);
    } catch (err) {
      if (requestDeliveryScope !== activeDeliveryScopeRef.current || requestDeliveryScope !== deliveryScope()) return;
      console.error("Erro ao carregar mais msgs:");
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (requestDeliveryScope === activeDeliveryScopeRef.current && requestDeliveryScope === deliveryScope()) setIsLoadingMore(false);
    }
  }, [contactPhone, messages, hasMore, isLoading, isLoadingMore, pageSize]);

  const updateMessageTracking = useCallback((
    localMessageId: string,
    providerMessageId?: string | null,
    status?: OptimizedMessage['status'],
    sentContent?: string
  ) => {
    if (!contactPhone || !localMessageId || renderedDeliveryScope !== activeDeliveryScopeRef.current
      || renderedDeliveryScope !== deliveryScope()) return;

    setMessages(prev => {
      const updated = prev.map(message => {
        const maybeLocal = message as OptimizedMessage & { localMessageId?: string };
        if (message.id !== localMessageId && maybeLocal.localMessageId !== localMessageId) {
          return message;
        }

        return mergePendingDelivery({
          ...message,
          providerMessageId: providerMessageId || message.providerMessageId,
          ...mergeMessageDelivery(message, { status }),
          ...(typeof sentContent === 'string' ? { content: ['image', 'video', 'audio'].includes(message.type) ? processMediaContent(sentContent, message.type) : sentContent } : {})
        });
      });
      messageCacheManager.saveMessages(contactPhone, updated);
      return updated;
    });
  }, [contactPhone]);

  const sendMessage = useCallback((
    content: any,
    type: 'text' | 'image' | 'audio' | 'video' | 'nps' | 'comment' = 'text',
    localMessageId?: string,
    source_channel?: string,
    extra?: Partial<OptimizedMessage>
  ) => {

    console.log("[useOptimizedMessages] Conteúdo recebido:");

    if (!contactPhone || renderedDeliveryScope !== activeDeliveryScopeRef.current || renderedDeliveryScope !== deliveryScope()) {
      console.warn("[useOptimizedMessages] sendMessage: contactPhone é null, abortando");
      return;
    }

    const processedContent = (type === 'image' || type === 'video' || type === 'audio')
      ? processMediaContent(content, type)
      : content;

    console.log("[useOptimizedMessages] Conteúdo processado:");

    // Gerar ID único se não foi fornecido
    const messageId = localMessageId || `local_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;


    // Adicionar ID ao conjunto de processados
    processedLocalIds.current.add(messageId);

    // Limpar ID após 5 minutos para evitar vazamento de memória
    setTimeout(() => {
      processedLocalIds.current.delete(messageId);
    }, 5 * 60 * 1000);

    const newMsg: OptimizedMessage = {
      id: messageId,
      type,
      content: processedContent,
      sender: {
        phone: 'me',
        name: 'Você',
        photo: ''
      },
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      timestampNumber: Date.now(),
      fromMe: true,
      status: 'sending',
      author_type: 'platform_human',
      author_name: 'Você',
      localMessageId: messageId, // Adicionar campo para rastreamento
      source_channel: source_channel, // ✅ Adicionar source_channel para identificar API Oficial
      ...extra
    } as OptimizedMessage & { localMessageId?: string };

    console.log("[useOptimizedMessages] Adicionando nova mensagem ao estado...");
    setMessages(prev => {

      const updated = dedupeAndSortMessages([...prev, newMsg]);


      messageCacheManager.saveMessages(contactPhone, updated);
      return updated;
    });

    return { ...newMsg, localMessageId: messageId };
  }, [contactPhone]);

  return {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMoreMessages,
    updateMessageTracking,
    sendMessage
  };
};
